import { Component, ElementRef, QueryList, ViewChildren } from '@angular/core';

import { CalendarMonthYearHeaderComponent } from 'src/app/components/calendar-month-year-header/calendar-month-year-header.component';
import { TimeParserService } from 'src/app/components/time-date/time-parser.service';
import { AttributeDataService } from '../../services/attribute-data.service';
import { ComponentsService } from '../../services/components.service';
import { TemplateEditorUtilsService } from '../../services/template-editor-utils.service';

@Component({
  selector: 'template-component-counter',
  templateUrl: './counter.component.html',
  styleUrls: ['./counter.component.scss'],
  standalone: false,
})
export class CounterComponent {
  static readonly CounterType = {
    DOWN: 'down',
    UP: 'up',
    DAILY: 'daily',
  } as const;

  readonly CounterType = CounterComponent.CounterType;

  private static readonly DEFAULT_TARGET_TIME = '00:00';

  // Only the persisted direction (Down or Up) plus Daily are ever offered —
  // switching *between* Down and Up isn't, since whether an existing
  // template's own rendering script reacts correctly to that isn't knowable
  // from this panel (some hardcode their own down-only date math). Daily is
  // safe regardless of direction because it never changes the persisted type
  // (see persistedType below). Matches the template's own conditional
  // rendering of exactly these two buttons.
  private get counterTypeOrder(): string[] {
    return [this.persistedType, CounterComponent.CounterType.DAILY];
  }

  @ViewChildren('counterTypeBtn')
  counterTypeBtns!: QueryList<ElementRef<HTMLButtonElement>>;

  public componentId!: string;
  public counterType: string = CounterComponent.CounterType.DOWN;
  public targetDate: Date | null = null;
  /** Time-of-day value for mat-timepicker (Date; only hours/minutes are used). */
  public targetTime: Date;
  public completionMessage: string = '';
  /** Template-author-controlled flag (the `non-completion` attribute) — hides
   *  the completion message entirely for templates that don't use it. Not
   *  user-editable; base counter never writes this attribute. */
  public nonCompletion: boolean = false;

  readonly calendarMonthYearHeader = CalendarMonthYearHeaderComponent;
  readonly matDatepickerConfig = {
    startView: 'month' as const,
    startAt: new Date(),
  };

  /**
   * The direction (`down`/`up`) last known for this component. rise-data-counter
   * has no "daily" type — only Down/Up carry direction — so this is preserved
   * across a switch to Daily rather than normalized, to avoid silently changing
   * a Daily counter's computed direction for existing presentations. Also read
   * by the template to decide which single direction button (Down or Up) to
   * render alongside Daily.
   */
  public persistedType: string = CounterComponent.CounterType.DOWN;

  constructor(
    private elementRef: ElementRef,
    private componentsService: ComponentsService,
    private attributeDataService: AttributeDataService,
    private templateEditorUtils: TemplateEditorUtilsService,
    private timeParserService: TimeParserService,
  ) {
    this.targetTime = this.timeParserService.midnight();

    this.componentsService.registerDirective({
      type: 'rise-data-counter',
      element: this.elementRef.nativeElement,
      show: () => {
        const selected = this.componentsService.selected as {
          id: string;
        } | null;
        if (selected?.id) {
          this.componentId = selected.id;
          this.load();
        }
      },
      getTitle: (component) => {
        return (
          'Count ' +
          this.templateEditorUtils.capitalizeFirstLetter(
            component.attributes.type.value,
          )
        );
      },
    });
  }

  load() {
    const storedType = this.attributeDataService.getAvailableAttributeData(
      this.componentId,
      'type',
    );
    const storedDate = this.attributeDataService.getAvailableAttributeData(
      this.componentId,
      'date',
    );
    const storedTime = this.attributeDataService.getAvailableAttributeData(
      this.componentId,
      'time',
    );
    const storedNonCompletion =
      this.attributeDataService.getAvailableAttributeData(
        this.componentId,
        'non-completion',
      );
    const storedCompletion =
      this.attributeDataService.getAvailableAttributeData(
        this.componentId,
        'completion',
      );

    this.persistedType = this.resolveType(storedType);
    this.nonCompletion = storedNonCompletion === '';
    this.completionMessage =
      typeof storedCompletion === 'string' ? storedCompletion : '';

    this.loadTargetDateTime(storedDate, storedTime);

    // A stored date always wins; a time-only config (no date) is Daily,
    // matching rise-data-counter's own `!date && time` convention. Applies
    // the same way regardless of direction — Daily never changes the
    // persisted type either way.
    this.counterType =
      !storedDate && storedTime
        ? CounterComponent.CounterType.DAILY
        : this.persistedType;
  }

  save() {
    if (this.counterType !== CounterComponent.CounterType.DAILY) {
      this.persistedType = this.counterType;
    }
    this.attributeDataService.setAttributeData(
      this.componentId,
      'type',
      this.persistedType,
    );

    this.applyTargetDateTime();

    if (
      this.persistedType !== CounterComponent.CounterType.UP &&
      !this.nonCompletion
    ) {
      this.attributeDataService.setAttributeData(
        this.componentId,
        'completion',
        this.completionMessage || '',
      );
    }
  }

  /**
   * Writes date/time for rise-data-counter.
   * - daily: date null + time (intentional time-only path)
   * - down/up with a target date: date + time
   * - down/up without a target date: explicit null for both, so no stale
   *   date/time value lingers in this component's flat attribute storage.
   */
  private applyTargetDateTime(): void {
    if (this.counterType === CounterComponent.CounterType.DAILY) {
      this.attributeDataService.setAttributeData(
        this.componentId,
        'date',
        null,
      );
      this.attributeDataService.setAttributeData(
        this.componentId,
        'time',
        this.timeForService(),
      );
      return;
    }

    if (this.targetDate) {
      this.attributeDataService.setAttributeData(
        this.componentId,
        'date',
        this.dateForService(),
      );
      this.attributeDataService.setAttributeData(
        this.componentId,
        'time',
        this.timeForService(),
      );
      return;
    }

    this.attributeDataService.setAttributeData(this.componentId, 'date', null);
    this.attributeDataService.setAttributeData(this.componentId, 'time', null);
  }

  private resolveType(stored: string | undefined): string {
    return stored === CounterComponent.CounterType.UP
      ? CounterComponent.CounterType.UP
      : CounterComponent.CounterType.DOWN;
  }

  private loadTargetDateTime(
    storedDate: string | null | undefined,
    storedTime: string | null | undefined,
  ): void {
    if (storedDate) {
      const localDate = new Date(storedDate);
      localDate.setMinutes(
        localDate.getMinutes() + localDate.getTimezoneOffset(),
      );
      this.targetDate = localDate;
    } else {
      this.targetDate = null;
    }

    const parsedTime = storedTime
      ? this.timeParserService.parseMilitary(storedTime)
      : null;
    this.targetTime = parsedTime || this.timeParserService.midnight();
  }

  /** rise-data-counter expects YYYY-MM-DD. Caller must ensure targetDate is set. */
  private dateForService(): string {
    const localDate = new Date(this.targetDate as Date);
    localDate.setMinutes(
      localDate.getMinutes() - localDate.getTimezoneOffset(),
    );
    return this.templateEditorUtils.formatISODate(localDate);
  }

  /** rise-data-counter expects HH:mm. */
  private timeForService(): string {
    if (!this.targetTime) {
      return CounterComponent.DEFAULT_TARGET_TIME;
    }
    return this.timeParserService.parseDateMilitary(this.targetTime);
  }

  setCounterType(type: string): void {
    if (type === this.counterType) return;
    this.counterType = type;
    if (type === CounterComponent.CounterType.DAILY) {
      this.targetDate = null;
    } else if (
      type === CounterComponent.CounterType.UP &&
      (!this.targetDate || this.targetDate > new Date())
    ) {
      const thirtyDaysBack = new Date();
      thirtyDaysBack.setDate(thirtyDaysBack.getDate() - 30);
      thirtyDaysBack.setHours(0, 0, 0, 0);
      this.targetDate = thirtyDaysBack;
    } else if (type === CounterComponent.CounterType.DOWN) {
      const startOfToday = new Date();
      startOfToday.setHours(0, 0, 0, 0);
      if (!this.targetDate || this.targetDate < startOfToday) {
        const thirtyDaysOut = new Date();
        thirtyDaysOut.setDate(thirtyDaysOut.getDate() + 30);
        thirtyDaysOut.setHours(0, 0, 0, 0);
        this.targetDate = thirtyDaysOut;
      }
    }
    this.save();
  }

  /**
   * Handles keyboard navigation within the counter type radiogroup.
   * Implements the ARIA radiogroup pattern: arrow keys move focus and
   * selection, only the selected option is in the tab sequence.
   */
  onCounterTypeKeydown(event: KeyboardEvent, type: string): void {
    const idx = this.counterTypeOrder.indexOf(type);
    let nextIdx: number | null = null;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        nextIdx = (idx + 1) % this.counterTypeOrder.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        nextIdx =
          (idx - 1 + this.counterTypeOrder.length) %
          this.counterTypeOrder.length;
        break;
      case 'Home':
        nextIdx = 0;
        break;
      case 'End':
        nextIdx = this.counterTypeOrder.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.setCounterType(this.counterTypeOrder[nextIdx]);
    this.counterTypeBtns.get(nextIdx)?.nativeElement.focus();
  }
}
